import { Navigate, type RouteObject } from 'react-router';

import Loader from '@@components/Loader';
import config, { type NavigationItem } from '@@config';
import AppWrapper from '@@containers/App/AppWrapper';
import { ErrorPage } from '@@containers/App/ErrorPage';
import ModalProvider from '@@containers/Modal/useModal/ModalProvider';
import PrivateRoute from '@@router/components/PrivateRoute';
import { routes as articleRoutes } from '@@routes/articles/routes';
import { routes as breakingnewsRoutes } from '@@routes/breakingnews/routes';
import { routes as categoriesRoutes } from '@@routes/categories/routes';
import { routes as collectionsRoutes } from '@@routes/collections/routes';
import { routes as curatedListRoutes } from '@@routes/curatedList/routes';
import { routes as embedsRoutes } from '@@routes/externalContent/embeds/routes';
import { routes as linksRoutes } from '@@routes/externalContent/links/routes';
import { routes as videoRoutes } from '@@routes/externalContent/videos/routes';
import { routes as geneeaEntitiesRoutes } from '@@routes/geneeaEntities/routes';
import { routes as homeRoutes } from '@@routes/home/routes';
import isolatedRouteConfig from '@@routes/isolated/config';
import { routes as isolatedRoutes } from '@@routes/isolated/routes';
import { routes as kickwordsRoutes } from '@@routes/kickwords/routes';
import { routes as maintenancenotesRoutes } from '@@routes/maintenancenotes/routes';
import { routes as newslettersRoutes } from '@@routes/newsletters/routes';
import { routes as placementRoutes } from '@@routes/placement/routes';
import { routes as plateRoutes } from '@@routes/plate/routes';
import { routes as previewRoutes } from '@@routes/preview/routes';
import { routes as releasenotesRoutes } from '@@routes/releasenotes/routes';
import { routes as rtElementsRoutes } from '@@routes/rtElements/routes';
import { routes as sitemapRoutes } from '@@routes/sitemap/routes';
import { routes as slideshowRoutes } from '@@routes/slideshow/routes';
import { routes as tagsRoutes } from '@@routes/tags/routes';
import { routes as teamsRoutes } from '@@routes/teams/routes';
import { routes as tickerRoutes } from '@@routes/ticker/routes';
import { routes as usersRoutes } from '@@routes/users/routes';
import { routes as userSettingsRoutes } from '@@routes/userSettings/routes';

const nav = Object.entries(config.navigation).reduce<Record<string, NavigationItem>>(
    (previousValue, [key, value]) => ({
        ...previousValue,
        [key]: {
            ...value,
            path: `${value.uri}/*`,
        },
    }),
    {},
);

const subRoutes: RouteObject[] = [
    {
        element: <PrivateRoute />,
        children: homeRoutes,
    },
    {
        path: 'placement',
        element: <PrivateRoute {...nav.placement} />,
        children: placementRoutes,
    },
    {
        path: 'breakingnews',
        element: <PrivateRoute {...nav.breakingnews} />,
        children: breakingnewsRoutes,
    },
    {
        path: 'slideshows',
        element: <PrivateRoute {...nav.slideshows} />,
        children: slideshowRoutes,
    },
    {
        path: 'externalContent/videos',
        element: <PrivateRoute />,
        children: videoRoutes,
    },
    {
        path: 'externalContent/embeds',
        element: <PrivateRoute />,
        children: embedsRoutes,
    },
    {
        path: 'externalContent/links',
        element: <PrivateRoute />,
        children: linksRoutes,
    },
    {
        path: 'articles',
        element: <PrivateRoute {...nav.articles} />,
        children: articleRoutes,
    },
    {
        path: 'tickers',
        element: <PrivateRoute {...nav.tickers} />,
        children: tickerRoutes,
    },
    {
        path: 'categories',
        element: <PrivateRoute {...nav.categories} />,
        children: categoriesRoutes,
    },
    {
        path: 'teams',
        element: <PrivateRoute {...nav.teams} />,
        children: teamsRoutes,
    },
    {
        path: 'tags',
        element: <PrivateRoute {...nav.tags} />,
        children: tagsRoutes,
    },
    {
        path: 'geneea-entities',
        element: <PrivateRoute {...nav.geneeaEntities} />,
        children: geneeaEntitiesRoutes,
    },
    {
        path: 'newsletters',
        element: <PrivateRoute {...nav.newsletters} />,
        children: newslettersRoutes,
    },
    {
        path: 'kickwords',
        element: <PrivateRoute {...nav.kickwords} />,
        children: kickwordsRoutes,
    },
    {
        path: 'sitemaps',
        element: <PrivateRoute {...nav.sitemaps} />,
        children: sitemapRoutes,
    },
    {
        path: 'collections',
        element: <PrivateRoute {...nav.collections} />,
        children: collectionsRoutes,
    },
    {
        path: 'releasenotes',
        element: <PrivateRoute {...nav.releasenotes} />,
        children: releasenotesRoutes,
    },
    {
        path: 'maintenancenotes',
        element: <PrivateRoute {...nav.maintenancenotes} />,
        children: maintenancenotesRoutes,
    },
    {
        path: 'preview',
        element: <PrivateRoute />,
        children: previewRoutes,
    },
    {
        path: 'userSettings',
        element: <PrivateRoute />,
        children: userSettingsRoutes,
    },
    {
        path: 'users',
        element: <PrivateRoute {...nav.users} />,
        children: usersRoutes,
    },
    {
        path: 'curatedList',
        element: <PrivateRoute {...nav.curatedList} />,
        children: curatedListRoutes,
    },
    {
        path: 'rtelements',
        element: <PrivateRoute {...nav.rtelements} />,
        children: rtElementsRoutes,
    },
    {
        path: 'login',
        lazy: async () => {
            const { Login } = await import('@@auth/Login');

            return { Component: Login };
        },
    },
    {
        path: '401',
        lazy: async () => {
            const { Error401 } = await import('@@containers/App/Error401');

            return { Component: Error401 };
        },
    },
    {
        path: '403',
        lazy: async () => {
            const { Error403 } = await import('@@containers/App/Error403');

            return { Component: Error403 };
        },
    },
    {
        path: '404',
        lazy: async () => {
            const { Error404 } = await import('@@containers/App/Error404');

            return { Component: Error404 };
        },
    },
    {
        path: '*',
        element: <Navigate to="/404" />,
    },
];

if (config.isVisualTest || config.mode === 'development') {
    const isolatedRoute = {
        path: isolatedRouteConfig.path,
        element: <PrivateRoute />,
        children: isolatedRoutes,
    };

    subRoutes.push(isolatedRoute);
}

if (config.env !== 'prod' && config.env !== 'production') {
    const plateRoute: RouteObject = {
        path: 'plate',
        element: <PrivateRoute />,
        children: plateRoutes,
    };

    subRoutes.push(plateRoute);
}

export const routes: RouteObject[] = [
    {
        path: '/',
        element: (
            // `ModalProvider` needs to be a child of the router since some modals use hooks which depend on it
            <ModalProvider>
                <AppWrapper />
            </ModalProvider>
        ),
        errorElement: <ErrorPage />,
        hydrateFallbackElement: <Loader />,
        children: subRoutes,
    },
];
